import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { EntityCombobox } from '@/components/shared/entity-combobox';
import { api, type OffsetPage } from '@/lib/api';
import type { PopoverPortalTarget } from '@/components/ui/popover-portal';

interface PlatformChurchRow {
  church: {
    id: string;
    name: string;
    campusName: string | null;
  };
}

export const ChurchCombobox = ({
  value,
  onChange,
  selectedLabel,
  placeholder,
  emptyMessage,
  clearLabel,
  id,
  popoverPortal,
}: {
  value: string | null;
  onChange: (churchId: string | null) => void;
  selectedLabel?: string | null;
  placeholder: string;
  emptyMessage: string;
  clearLabel: string;
  id?: string;
  popoverPortal?: PopoverPortalTarget;
}) => {
  const [search, setSearch] = useState('');

  const churches = useQuery({
    queryKey: ['platform-churches-picker', search],
    queryFn: () =>
      api.get<{ data: PlatformChurchRow[]; page: OffsetPage }>('/platform/churches', {
        query: {
          page: 1,
          pageSize: 20,
          sort: 'name',
          ...(search.trim() ? { search: search.trim() } : {}),
        },
      }),
  });

  const options = useMemo(
    () =>
      (churches.data?.data ?? []).map((row) => ({
        id: row.church.id,
        label: row.church.campusName
          ? `${row.church.name} · ${row.church.campusName}`
          : row.church.name,
      })),
    [churches.data],
  );

  const selected = value
    ? (options.find((option) => option.id === value) ??
      (selectedLabel ? { id: value, label: selectedLabel } : null))
    : null;

  return (
    <EntityCombobox
      value={value}
      onChange={onChange}
      options={options}
      selected={selected}
      onQueryChange={setSearch}
      loading={churches.isLoading}
      placeholder={placeholder}
      emptyMessage={emptyMessage}
      clearLabel={clearLabel}
      id={id}
      popoverPortal={popoverPortal}
    />
  );
};
